import { useMemo } from 'react'
import { CryptoChart } from 'components/features/rewards/rewardPage/store'
import { mapChartItemToPoints } from 'components/features/rewards/rewardPage/helpers'
import moment from 'moment'
import { useChart } from 'components/atoms/chart/useChart'
import { scale } from 'd3'

export type Size = { width: number, height: number }

export const useCryptoChartContext = (data: CryptoChart, chartSize: Size) => {

  const { usdPoints, cryptoPoints, chartPoints, monthlyUsdPoints } = useMemo(() => {

    const usdPointsLocal = data.usd.map(mapChartItemToPoints)
    const cryptoPointsLocal = data.crypto.map(mapChartItemToPoints)

    const chartPointsLocal = [
      ...usdPointsLocal,
      ...cryptoPointsLocal,
    ].sort((a, b) => {
      return a[0] - b[0]
    })

    const monthlyUsdPointsLocal = data.usd
      .filter(({ date }) => moment(date).date() === 1)
      .map(mapChartItemToPoints)

    const monthlyCryptoPointsLocal = data.crypto
      .filter(({ date }) => moment(date).date() === 1)
      .map(mapChartItemToPoints)

    return {
      usdPoints: usdPointsLocal,
      cryptoPoints: cryptoPointsLocal,
      chartPoints: chartPointsLocal,
      monthlyUsdPoints: monthlyUsdPointsLocal,
      monthlyCryptoPoints: monthlyCryptoPointsLocal,
    }
  }, [data.crypto, data.usd])


  const usdContext = useChart(usdPoints, chartSize.width, chartSize.height)
  const cryptoContext = useChart(cryptoPoints, chartSize.width, chartSize.height)
  const chartContext = useChart(chartPoints, chartSize.width, chartSize.height)
  const monthlyUsdContext = useChart(monthlyUsdPoints, chartSize.width, chartSize.height)

  useMemo(() => {
    const [firstDate, lastDate] = usdContext.xRange
    const range = [
      firstDate,
      moment(lastDate).endOf('month').valueOf(),
    ]

    const scaleX = scale.linear()
      .domain(range)
      .range([0, chartSize.width])

    usdContext.scaleX = scaleX
    cryptoContext.scaleX = scaleX
    chartContext.scaleX = scaleX
    monthlyUsdContext.scaleX = scaleX
  }, [chartContext, cryptoContext, chartSize.width, usdContext, monthlyUsdContext])

  const { usdLineContext, cryptoLineContext } = useMemo(() => ({

    usdLineContext: {
      ...chartContext,
      points: usdContext.points,
    },
    cryptoLineContext: {
      ...chartContext,
      points: cryptoContext.points,
    },
  }), [chartContext, cryptoContext.points, usdContext.points])

  return {
    usdContext,
    cryptoContext,
    chartContext,
    monthlyUsdContext,
    usdLineContext,
    cryptoLineContext,
  }
}

export type CryptoChartsContexts = ReturnType<typeof useCryptoChartContext>
